/**
 * /legal/trust-centre — Hub for trust and compliance signals.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { LegalLayout } from '../../components/layouts/legal-layout';
import { Shield, Lock, FileText, AlertCircle, Database } from 'lucide-react';

export const Route = createFileRoute('/legal/trust-centre')({
  component: TrustCentrePage,
});

interface TrustCard {
  icon: React.ReactNode;
  title: string;
  body: string;
  to: string;
  linkLabel: string;
}

const CARDS: TrustCard[] = [
  {
    icon: <Lock size={20} />,
    title: 'Security disclosure',
    body: 'Data residency, encryption standards, audit chain integrity, and our vulnerability disclosure programme.',
    to: '/legal/security',
    linkLabel: 'Read security disclosure',
  },
  {
    icon: <Database size={20} />,
    title: 'Sub-processors',
    body: 'Every third-party service we use to process personal information, with purpose, location, and data category.',
    to: '/legal/subprocessors',
    linkLabel: 'View sub-processors',
  },
  {
    icon: <Shield size={20} />,
    title: 'Compliance',
    body: 'POPIA compliance status, FICA s23 record retention, and beneficial ownership lodge confirmation.',
    to: '/legal/trust-centre#compliance',
    linkLabel: 'Compliance status',
  },
  {
    icon: <AlertCircle size={20} />,
    title: 'Incident history',
    body: 'A transparent record of any past security incidents, including severity, timeline, and remediation.',
    to: '/legal/trust-centre#incidents',
    linkLabel: 'Incident log',
  },
  {
    icon: <FileText size={20} />,
    title: 'POPIA artefacts',
    body: 'Information Officer appointment, processing register, and the PAIA manual for record access requests.',
    to: '/legal/paia',
    linkLabel: 'PAIA manual',
  },
];

function TrustCentrePage() {
  return (
    <LegalLayout heading="Trust Centre." lastUpdated="7 May 2026">
      <p>
        Trust administration handles some of the most sensitive personal and financial records in
        a family&apos;s life. This page is the permanent home of Elreign&apos;s security posture,
        compliance artefacts, and sub-processor list. If something is not here that you need for
        due diligence, contact us at{' '}
        <a href="mailto:security@elreign.io" style={{ color: 'var(--primary-600)' }}>
          security@elreign.io
        </a>
        .
      </p>

      {/* Cards */}
      <div
        style={{
          display: 'grid',
          // Design gate F1: a card never wider than its column on a phone.
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))',
          gap: 20,
          marginTop: 36,
        }}
      >
        {CARDS.map((card) => (
          <div
            key={card.to}
            className="card"
            style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <div style={{ color: 'var(--primary-600)', display: 'flex', alignItems: 'center' }}>
              {card.icon}
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink)', margin: '0 0 8px', letterSpacing: '-0.01em' }}>
                {card.title}
              </h3>
              <p style={{ fontSize: 14.5, color: 'var(--ink-3)', lineHeight: 1.55, margin: 0 }}>
                {card.body}
              </p>
            </div>
            <Link
              to={card.to as never}
              className="card-link"
              style={{ marginTop: 'auto', display: 'inline-block', fontSize: 14 }}
            >
              {card.linkLabel}
            </Link>
          </div>
        ))}
      </div>

      <h2 id="compliance" style={{ marginTop: 56 }}>Compliance status</h2>
      <table className="legal-table">
        <thead>
          <tr>
            <th>Requirement</th>
            <th>Status</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>POPIA: Information Officer designated</td>
            <td><StatusBadge label="Designated" tone="pos" /></td>
            <td>Designated per s. 55/56, contactable at info@elreign.io. Regulator registration follows ahead of first production processing.</td>
          </tr>
          <tr>
            <td>POPIA: Processing activities register</td>
            <td><StatusBadge label="Maintained" tone="pos" /></td>
            <td>Maintained by the Information Officer per s. 17, alongside the Personal Information Impact Assessment.</td>
          </tr>
          <tr>
            <td>FICA s23: 5-year record retention</td>
            <td><StatusBadge label="Implemented" tone="pos" /></td>
            <td>Immutable WORM audit trail enforced at the storage layer.</td>
          </tr>
          <tr>
            <td>PAIA: s51 manual published</td>
            <td><StatusBadge label="Published" tone="pos" /></td>
            <td>This manual constitutes the required s51 publication.</td>
          </tr>
          <tr>
            <td>POPIA s72(1)(c): Data residency</td>
            <td><StatusBadge label="Compliant" tone="pos" /></td>
            <td>All data stored in Azure South Africa North (ZA primary, ZA secondary fallback).</td>
          </tr>
        </tbody>
      </table>

      <h2 id="incidents">Incident history</h2>
      <p>
        No security incidents have been recorded. This section will be updated with full transparency
        if any incident occurs, including severity classification, timeline of detection and
        remediation, and any personal information affected.
      </p>
    </LegalLayout>
  );
}

type BadgeTone = 'pos' | 'warn' | 'neg';
function StatusBadge({ label, tone }: { label: string; tone: BadgeTone }) {
  const map: Record<BadgeTone, { bg: string; fg: string }> = {
    pos:  { bg: 'var(--positive-soft)', fg: 'var(--positive)' },
    warn: { bg: 'var(--accent-100)',     fg: 'var(--accent-700)' },
    neg:  { bg: 'var(--negative-soft)', fg: 'var(--negative)' },
  };
  const c = map[tone];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      background: c.bg, color: c.fg,
      padding: '3px 10px', borderRadius: 999,
      fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
    }}>
      {label}
    </span>
  );
}
